/** Host detection for browser / PWA vs pywebview desktop shell. */

export type DesktopUpdateStatus = {
  packaged: boolean;
  current_version: string;
  pending: {
    version: string;
    notes?: string;
    banner_hidden?: boolean;
    apply_failed?: boolean;
    apply_failed_error?: string;
    current_version?: string;
  } | null;
  apply_failed: { version: string; error?: string } | null;
  server_unreachable?: boolean;
  show_banner?: boolean;
};

export type DesktopApplyUpdateResult = {
  ok: boolean;
  error?: string;
  version?: string;
  current_version?: string;
};

export type FrogsworkDesktopHost = {
  /** Override API base (e.g. staging). */
  apiBase?: string;
  /** Open URL in the system browser (injected by the shell). */
  openExternal?: (url: string) => void;
  /** Mirror tokens into AppData for Windows uninstall export. */
  persistSession?: (accessToken: string, refreshToken?: string, email?: string) => unknown;
  clearSession?: () => unknown;
  getAppVersion?: () => unknown;
  isPackaged?: () => unknown;
  getUpdateStatus?: (forceCheck?: boolean) => unknown;
  dismissUpdate?: (version: string) => unknown;
  applyUpdate?: () => unknown;
};

declare global {
  interface Window {
    frogsworkDesktop?: FrogsworkDesktopHost | true;
    pywebview?: {
      api?: {
        open_external?: (url: string) => unknown;
        get_api_base?: () => string;
        get_app_version?: () => unknown;
        is_packaged?: () => unknown;
        get_update_status?: (forceCheck?: boolean) => unknown;
        dismiss_update?: (version: string) => unknown;
        apply_update?: () => unknown;
        persist_session?: (
          accessToken: string,
          refreshToken?: string,
          email?: string
        ) => unknown;
        clear_session?: () => unknown;
      };
    };
  }
}

async function awaitHostValue<T>(value: unknown): Promise<T> {
  return (await Promise.resolve(value)) as T;
}

function syncFromPywebviewApi(): FrogsworkDesktopHost | null {
  const api = window.pywebview?.api;
  if (!api) return null;
  const host: FrogsworkDesktopHost = {
    openExternal: (url: string) => {
      if (api.open_external) api.open_external(url);
    },
    persistSession: (accessToken, refreshToken, email) => {
      if (api.persist_session) {
        return api.persist_session(accessToken || "", refreshToken || "", email || "");
      }
      return false;
    },
    clearSession: () => {
      if (api.clear_session) return api.clear_session();
      return false;
    },
    getAppVersion: () => (api.get_app_version ? api.get_app_version() : ""),
    isPackaged: () => (api.is_packaged ? api.is_packaged() : false),
    getUpdateStatus: (forceCheck) =>
      api.get_update_status ? api.get_update_status(Boolean(forceCheck)) : null,
    dismissUpdate: (version) =>
      api.dismiss_update ? api.dismiss_update(version || "") : false,
    applyUpdate: () =>
      api.apply_update
        ? api.apply_update()
        : { ok: false, error: "Updater unavailable" },
  };
  try {
    const base = api.get_api_base?.();
    if (base) host.apiBase = String(base).replace(/\/$/, "");
  } catch {
    /* ignore */
  }
  window.frogsworkDesktop = host;
  return host;
}

export function isDesktopHost(): boolean {
  return Boolean(window.frogsworkDesktop) || Boolean(window.pywebview);
}

export function desktopHostConfig(): FrogsworkDesktopHost | null {
  if (window.frogsworkDesktop && window.frogsworkDesktop !== true) {
    return window.frogsworkDesktop;
  }
  const fromApi = syncFromPywebviewApi();
  if (fromApi) return fromApi;
  if (window.frogsworkDesktop === true || window.pywebview) return {};
  return null;
}

/** Apply body classes and strip PWA install chrome when running in the desktop shell. */
export function applyHostEnvironment(): void {
  if (!isDesktopHost()) return;
  document.body.classList.add("host-desktop");
  document.documentElement.classList.add("host-desktop");
  const manifest = document.querySelector('link[rel="manifest"]');
  if (manifest) manifest.remove();
  syncFromPywebviewApi();
}

/** Prefer the system browser for http(s) links when the shell provides openExternal. */
export function openExternalUrl(url: string): boolean {
  const cfg = desktopHostConfig();
  if (cfg?.openExternal && /^https?:\/\//i.test(url)) {
    try {
      cfg.openExternal(url);
      return true;
    } catch {
      /* fall through */
    }
  }
  return false;
}

/** Wire clicks on http(s) anchors to the system browser when in the desktop shell. */
export function wireExternalLinks(root: ParentNode = document): void {
  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    if (!/^https?:\/\//i.test(href)) return;
    if (openExternalUrl(href)) {
      event.preventDefault();
    }
  });
}

/** Listen for late pywebviewready so host classes apply even if the event fires after boot. */
export function watchPywebviewReady(): void {
  if (!window.pywebview) {
    window.addEventListener("pywebviewready", () => {
      syncFromPywebviewApi();
      applyHostEnvironment();
    });
  }
}

/** Push current localStorage tokens to the native shell (for uninstall export). */
export function syncSessionToDesktopHost(
  accessToken: string,
  refreshToken?: string,
  email?: string
): void {
  if (!isDesktopHost()) return;
  const cfg = desktopHostConfig();
  try {
    if (accessToken) {
      cfg?.persistSession?.(accessToken, refreshToken || "", email || "");
    } else {
      cfg?.clearSession?.();
    }
  } catch {
    /* ignore */
  }
}

export async function getDesktopUpdateStatus(
  forceCheck = false
): Promise<DesktopUpdateStatus | null> {
  if (!isDesktopHost()) return null;
  const cfg = desktopHostConfig();
  if (!cfg?.getUpdateStatus) return null;
  try {
    const status = await awaitHostValue<DesktopUpdateStatus | null>(
      cfg.getUpdateStatus(forceCheck)
    );
    return status || null;
  } catch {
    return null;
  }
}

export async function getDesktopAppVersion(): Promise<string> {
  const cfg = desktopHostConfig();
  if (!cfg?.getAppVersion) return "";
  try {
    const version = await awaitHostValue<string>(cfg.getAppVersion());
    return String(version || "");
  } catch {
    return "";
  }
}

export async function dismissDesktopUpdate(version: string): Promise<boolean> {
  const cfg = desktopHostConfig();
  if (!cfg?.dismissUpdate) return false;
  try {
    return Boolean(await awaitHostValue(cfg.dismissUpdate(version)));
  } catch {
    return false;
  }
}

export async function applyDesktopUpdate(): Promise<DesktopApplyUpdateResult> {
  const cfg = desktopHostConfig();
  if (!cfg?.applyUpdate) {
    return { ok: false, error: "Updater unavailable" };
  }
  try {
    return await awaitHostValue<DesktopApplyUpdateResult>(cfg.applyUpdate());
  } catch (ex) {
    return {
      ok: false,
      error: ex instanceof Error ? ex.message : "Update failed",
    };
  }
}
